
/** @jsxImportSource @gongxigames/framework/core/fragments/BuilderY */
/** @jsxRuntime automatic */

import {
    FC,
    ExtendContainerProps,
    $Container,
    getProp,
    $NineSlicePlane,
    RichTextStyleParams,
    JSX,
    PointLike,
} from '@gongxigames/framework/core/fragments/BuilderY';
import {Signal, Store} from '@gongxigames/framework/core/fragments/store/Store';
import {$RubyUIText} from '../../../JSX/RubyUIText';
import {$RubyInteractive} from '../../../JSX/RubyInteractive';
import {slotStore} from '@gongxigames/framework/slotV2/globals';
import {IPointData} from 'pixi.js';

const TEXT_PASSIVE_STYLE: RichTextStyleParams = {
    fontSize: 24*3,
    fill: 0xffffff
};

const TEXT_ACTIVE_STYLE: RichTextStyleParams = {
    fontSize: 24*3,
    fill: 0x000000
};

export type AutoplayRoundsSelectorProps = ExtendContainerProps<{
    options: number[];
    roundsCount: number;
    action: (value: number) => void;
}>;

export const $AutoplayRoundsSelector: FC<AutoplayRoundsSelectorProps> = (props) => {
    const {roundsCount, options, action} = props;

    return <$Container
        {...props}
        name={'AutoplayRoundsSelector'}
    >
        <$RubyUIText
            localeID={'autoplayRoundsTitle'}
            stylePreset='BASE_BOLD'
        />
        <$RoundsGroup
            active={roundsCount}
            options={options}
            action={action}
        />
    </$Container>;
};

type RoundGroupProps = ExtendContainerProps<{
    active: number;
    options: number[];
    action?: (value: number) => void;
}>;

const $RoundsGroup: FC<RoundGroupProps> = (props) => {
    const options = getProp(props.options);
    const action = getProp(props.action);

    const rounds: {
        round: JSX.Element;
        position: Signal<IPointData>;
        active: Signal<boolean>;
    }[] = new Array(options.length);

    const positionY = Store.signal(0);
    const scale = Store.signal(1);

    const bgHeight = 120;
    const bgWidth = Store.signal(0);
    const bgPivot = Store.signal({x: 0, y: 0});

    options.forEach((option, i) => {
        const active = Store.signal(false);
        const position = Store.signal({x: 0, y: 0});

        const round = $RoundComponent({
            roundsCount: option,
            position,
            active,
            bgWidth,
            bgHeight,
            bgPivot,
            scale,
            action: () => action?.(options[i])
        });

        rounds[i] = ({round, position, active});
    });

    const updateRoundsTransform = (w: number, s: number, isPortrait: boolean) => {
        const distanceX = isPortrait ? 290 : 260;
        const distanceY = 150;
        const elementsOnRow = Math.min(options.length, (isPortrait ? 3 : 6));

        bgWidth.value = w;
        scale.value = s;

        const gapX = -((elementsOnRow - 1) / 2) * distanceX;

        rounds.forEach((round, i) => {
            const row = Math.floor(i / elementsOnRow);
            const indexInRow = i - elementsOnRow * row;
            const x = gapX + indexInRow * distanceX;
            const y = row * distanceY;

            round.position.value = {x, y};
        });
    };

    const effectActive = Store.effect(() => {
        const index = options.findIndex((opt) => opt === getProp(props.active));

        rounds.forEach((r, i) => r.active.value = i === index);
    });

    const effectTransform = Store.effect(() => {
        if (slotStore.stage_isPortrait.value) {
            positionY.value = 130;
            updateRoundsTransform(260, 1, true);
        } else {
            positionY.value = 105;
            updateRoundsTransform(230, 0.95, false);
        }

        bgPivot.value = {x: bgWidth.value * 0.5, y: bgHeight * 0.5};
    });

    return <$Container
        y={positionY}
        events={{
            destroyed: [effectActive, effectTransform]
        }}
    >
        {...rounds.map(({round}) => round)}
    </$Container>;
};

type RoundComponentProps = ExtendContainerProps<{
    action?: () => void;
    roundsCount: number;
    bgWidth: number;
    bgHeight: number;
    bgPivot: PointLike;
    active: boolean;
}>;

const $RoundComponent: FC<RoundComponentProps> = (props)=> {
    const isOver = Store.signal(false);
    const textStyle = Store.computed(() => getProp(props.active) ? TEXT_ACTIVE_STYLE : TEXT_PASSIVE_STYLE);
    const bgAlpha = Store.computed(() => {
        if (getProp(props.active)) {
            return 1;
        }

        return isOver.value ? 0.3 : 0.2;
    });

    const onOver = () => isOver.value = true;
    const onOut = () => isOver.value = false;
    const onTap = () => getProp(props.action)?.();

    return <$RubyInteractive
        {...props}
        onOver={onOver}
        onOut={onOut}
        onTap={onTap}
    >
        <$NineSlicePlane
            texture='autoplay_rounds_selector_slice'
            slices={[0, 40]}
            alpha={bgAlpha}
            width={props.bgWidth}
            height={props.bgHeight}
            pivot={props.bgPivot}
        />
        <$RubyUIText
            text={`${getProp(props.roundsCount)}`}
            anchor={0.5}
            stylePreset='BASE_BOLD'
            tint={0xff0000}
            style={textStyle}
        />
    </$RubyInteractive>;
};
